import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import api from '../../api/axios';  // Adjust path as needed
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    Chip,
    CircularProgress,
    FormControl, 
    Grid,
    InputLabel, 
    Link,
    MenuItem,
    Select, 
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    TextField
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import moment from 'moment';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const WebsiteDetail = () => {  // Remove props
    const { id } = useParams();  // Add this
    const navigate = useNavigate();  // Add this
    const [website, setWebsite] = useState(null);  // Add this
    const [companyProfile, setCompanyProfile] = useState(null);
    const [contacts, setContacts] = useState([]);
    const [weeklyMetrics, setWeeklyMetrics] = useState([]);
    const [isGenerating, setIsGenerating] = useState(false);
    const [error, setError] = useState(null);
    const [message, setMessage] = useState(null);
    const [loading, setLoading] = useState(true);
    const [emailIntroduction, setEmailIntroduction] = useState(null);
    const [isGeneratingEmail, setIsGeneratingEmail] = useState(false);
    const [personalization, setPersonalization] = useState('');
    const [sendingEmail, setSendingEmail] = useState(false);
    const [emailStatus, setEmailStatus] = useState(null);
    const [processingContacts, setProcessingContacts] = useState({});
    const [selectedContactEmail, setSelectedContactEmail] = useState('');
    const [emailIntroductions, setEmailIntroductions] = useState([]);

    const handleSendIntroduction = async () => {
        try {
            // Add validation
            if (!selectedContactEmail) {
                setEmailStatus({
                    type: 'error',
                    message: 'Please select a contact to send the email to'
                });
                return;
            }
            const currentSubject = emailIntroduction?.email_content?.subject;
            const currentContent = emailIntroduction?.email_content?.content;
    
            if (!currentSubject?.trim()) {
                setEmailStatus({
                    type: 'error',
                    message: 'Cannot send: Email subject is required'
                });
                return;
            }
            
            if (!currentContent?.trim()) {
                setEmailStatus({
                    type: 'error',
                    message: 'Cannot send: Email content is required'
                });
                return;
            }
    
            setSendingEmail(true);
            setEmailStatus(null);
            
            console.log('Sending email with content:', {
                subject: currentSubject,
                content: currentContent
            });
            
            const csrfToken = document.cookie
                .split('; ')
                .find(row => row.startsWith('csrftoken='))
                ?.split('=')[1];
            
            const response = await fetch(
                `/api/websites/${website.id}/send-introduction/`,
                {
                    method: 'POST',
                    headers: {
                        'X-CSRFToken': csrfToken,
                        'Content-Type': 'application/json',
                    },
                    credentials: 'include',
                    body: JSON.stringify({
                        subject: currentSubject,
                        content: currentContent,
                        recipient_email: selectedContactEmail
                    })
                }
            );
            
            const data = await response.json();
            
            if (data.success) {
                setEmailStatus({
                    type: 'success',
                    message: 'Introduction email sent successfully!'
                });
            } else {
                setEmailStatus({
                    type: 'error',
                    message: `Failed to send email: ${data.error}`
                });
            }
        } catch (error) {
            setEmailStatus({
                type: 'error',
                message: `Error: ${error.message}`
            });
        } finally {
            setSendingEmail(false);
        }
    };

    const [newContact, setNewContact] = useState({
        name: '',
        email: '',
        title: '',
        phone: ''
    });

    // Move fetchData outside useEffect
    const fetchData = async () => {
        setLoading(true);
        try {
            // First fetch the website details
            const websiteRes = await axios.get(`/api/websites/${id}`);
            setWebsite(websiteRes.data);
            console.log('Website data:', websiteRes.data);

            // Then fetch related data
            const profileRes = await axios.get(`/api/company-profiles/?website_id=${id}`);
            console.log('Company profile response:', profileRes.data);
            if (profileRes.data.results?.length) {
                setCompanyProfile(profileRes.data.results[0]);
            }

            const metricsRes = await axios.get(`/api/weekly-metrics/?website_id=${id}`);
            setWeeklyMetrics(metricsRes.data.results || []);

            const contactsRes = await axios.get(`/api/contacts/?website_id=${id}`);
            setContacts(contactsRes.data.results || []);
            if (contactsRes.data.results && contactsRes.data.results.length > 0) {
                setSelectedContactEmail(contactsRes.data.results[0].email);
            }

        } catch (error) {
            console.error('Error fetching data:', error);
            setError('Failed to load website data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [id]);

    const handleAddContact = async () => {
        try {
            const response = await api.post(`/websites/${id}/contacts/`, newContact);
            console.log('New contact response:', response.data);  // Add this line
            fetchData();
            setNewContact({ name: '', email: '', title: '', phone: '' });
        } catch (error) {
            console.error('Error adding contact:', error);
        }
    };

    const handleDeleteContact = async (contactId) => {
        try {
            // Remove the '/api' prefix since it's handled by the api instance
            await api.delete(`/websites/${id}/contacts/?contact_id=${contactId}`);
            fetchData();
        } catch (error) {
            console.error('Error deleting contact:', error);
            setError('Failed to delete contact');
        }
    };

    const handleGenerateContactProfile = async (contactId) => {
        setProcessingContacts(prev => ({ ...prev, [contactId]: true }));
        try {
            // Note the URL change to match DRF's action pattern
            const response = await axios.post(`/api/contacts/${contactId}/generate_profile/`);
            
            if (response.data.status === 'success') {
                const contactsRes = await axios.get(`/api/contacts/?website_id=${id}`);
                setContacts(contactsRes.data.results || []);
                setMessage('Contact profile generated successfully');
            } else {
                throw new Error(response.data.message || 'Failed to generate profile');
            }
        } catch (error) {
            console.error('Error details:', error.response || error);
            setError(error.response?.data?.message || error.message || 'Failed to generate contact profile');
        } finally {
            setProcessingContacts(prev => ({ ...prev, [contactId]: false }));
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                // First fetch the website details
                const websiteRes = await axios.get(`/api/websites/${id}`);
                setWebsite(websiteRes.data);
                console.log('Website data:', websiteRes.data);

                // Then fetch related data
                const profileRes = await axios.get(`/api/company-profiles/?website_id=${id}`);
                console.log('Company profile response:', profileRes.data);
                if (profileRes.data.results?.length) {
                    setCompanyProfile(profileRes.data.results[0]);
                }

                const metricsRes = await axios.get(`/api/weekly-metrics/?website_id=${id}`);
                setWeeklyMetrics(metricsRes.data.results || []);

                const contactsRes = await axios.get(`/api/contacts/?website_id=${id}`);
                setContacts(contactsRes.data.results || []);

            } catch (error) {
                console.error('Error fetching data:', error);
                setError('Failed to load website data');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [id]);

    const formatEmailContent = (emailData) => {
        if (!emailData) return null;
        
        try {
            console.log('Raw email data:', emailData);
            
            if (typeof emailData.email_content === 'string') {
                // If it's still a string, try to parse it
                const content = emailData.email_content
                    .replace('```json', '')
                    .replace('```', '')
                    .trim();
                
                console.log('Cleaned content before parsing:', content);
                
                const parsedContent = JSON.parse(content);
                
                // Ensure line breaks are preserved
                if (parsedContent.content) {
                    parsedContent.content = parsedContent.content
                        .replace(/\\n/g, '\n')  // Replace escaped newlines
                        .replace(/\n/g, '<br>\n');  // Convert newlines to HTML breaks
                }
                
                console.log('Formatted content:', parsedContent);
                
                emailData.email_content = parsedContent;
            }
            return emailData;
        } catch (error) {
            console.error('Error formatting email content:', error);
            console.error('Error details:', error);
            return emailData;
        }
    };

    const fetchEmailIntroduction = async () => {
        try {
            if (!id) return;
            
            console.log('Fetching email introductions for website ID:', id);
            const response = await axios.get(`/api/email-introductions/?website_id=${id}`);
            console.log('Email introductions response:', response.data);
            
            if (response.data.results?.length) {
                // Get all emails ordered by date (most recent first)
                const allEmails = response.data.results;
                
                // Get sent emails for analytics table
                const sentEmails = allEmails.filter(email => email.status === 'sent');
                setEmailIntroductions(sentEmails);
                
                // Only set email for editing if the most recent email is pending
                const mostRecentEmail = allEmails[0]; // First email is most recent due to ordering
                if (mostRecentEmail && mostRecentEmail.status === 'pending') {
                    const formattedEmail = formatEmailContent(mostRecentEmail);
                    console.log('Latest pending email:', formattedEmail);
                    setEmailIntroduction(formattedEmail);
                } else {
                    setEmailIntroduction(null);
                }
            } else {
                console.log('No email introductions found for this website');
                setEmailIntroduction(null);
                setEmailIntroductions([]);
            }
        } catch (error) {
            console.error('Error fetching email introductions:', error);
        }
    };
    
    // Use this in both useEffect and after generation
    useEffect(() => {
        fetchEmailIntroduction();
    }, [id]);

    const handleBack = () => {  // Add this
        navigate('/websites');
    };

    const handleGenerateProfile = async () => {
        setIsGenerating(true);
        setError(null);
        setMessage(null);
        
        try {
            const response = await axios.post('/api/company-profiles/generate/', {
                website_id: website.id,
                domain: website.domain
            });
            
            if (response.data.status === 'accepted') {
                setMessage('Profile generation has started. Please refresh in a few minutes to see the results.');
                startPolling(website.id);
            } else {
                setError('Failed to start profile generation. Please try again.');
            }
        } catch (error) {
            console.error('Failed to generate profile:', error);
            setError(error.response?.data?.message || 'Failed to start profile generation. Please try again.');
        } finally {
            setIsGenerating(false);
        }
    };

    const startPolling = (websiteId) => {
        const pollInterval = setInterval(async () => {
            try {
                const profileRes = await axios.get(`/api/company-profiles/?website_id=${websiteId}`);
                if (profileRes.data.results?.[0]) {
                    setCompanyProfile(profileRes.data.results[0]);
                    setMessage('Profile generation completed!');
                    clearInterval(pollInterval);
                }
            } catch (error) {
                console.error('Error polling for profile:', error);
            }
        }, 10000);
        
        // Stop polling after 5 minutes
        setTimeout(() => {
            clearInterval(pollInterval);
        }, 300000);
    };

    const handleGenerateEmail = async () => {
        setIsGeneratingEmail(true);
        setError(null);
        try {
            const response = await axios.post('/api/email-introductions/generate/', {
                profile_id: id,
                personalization: personalization
            });
            
            console.log('Generate email response:', response.data);
    
            if (response.data.status === 'success' && response.data.email) {
                setEmailIntroduction(response.data.email);
                setMessage('Email introduction generated successfully');
            } else {
                // Try to fetch the email if not included in response
                await fetchEmailIntroduction();
            }
        } catch (error) {
            console.error('Error generating email:', error);
            setError(error.response?.data?.message || 'Failed to generate email introduction');
        } finally {
            setIsGeneratingEmail(false);
        }
    };
    
    // Add this debug function at the component level
    const logState = () => {
        console.log('Current State:', {
            websiteId: id,
            websiteData: website,
            companyProfileData: companyProfile,
            emailIntroductionData: emailIntroduction
        });
    };

    const quillStyles = {
        '.ql-editor': {
            whiteSpace: 'pre-wrap !important',
            lineHeight: '1.5',
            '& p': {
                marginBottom: '1em'
            }
        }
    };

    const handleSubjectChange = (e) => {
        setEmailIntroduction(prev => ({
            ...prev,
            email_content: {
                ...prev.email_content,
                subject: e.target.value
            }
        }));
    };
    
    const handleEmailContentChange = (content) => {
        console.log('New content:', content);
        setEmailIntroduction(prev => {
            const updated = {
                ...prev,
                email_content: {
                    ...prev.email_content,
                    content: content
                }
            };
            console.log('Updated email introduction:', updated);
            return updated;
        });
    };
    
    const handleSaveChanges = async () => {
        try {
            console.log('Saving email introduction...');
            console.log('Current state:', emailIntroduction);
    
            // Format the content properly
            const formattedContent = {
                subject: emailIntroduction.email_content.subject,
                content: emailIntroduction.email_content.content
            };
    
            // Create the markdown-wrapped JSON string
            const emailContentWithMarkdown = `\`\`\`json\n${JSON.stringify(formattedContent, null, 2)}\n\`\`\``;
            
            console.log('Sending to API:', emailContentWithMarkdown);
            console.log('Email ID:', emailIntroduction.id);
    
            const response = await axios.patch(
                `/api/email-introductions/${emailIntroduction.id}/`,
                { email_content: emailContentWithMarkdown },
                { headers: { 'Content-Type': 'application/json' } }
            );
            
            console.log('Save response:', response);
            
            if (response.data) {
                setMessage('Email introduction saved successfully');
                // Refresh the email data
                await fetchEmailIntroduction();
            } else {
                setError('No response data received');
            }
        } catch (error) {
            console.error('Save error:', error);
            console.error('Error response:', error.response?.data);
            setError(`Failed to save changes: ${error.message}`);
        }
    };

    if (loading || !website) {  // Update loading check
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box className="container" sx={{ mt: 4 }}>  {/* Add this wrapper */}
            <Box>
                <Button variant="outlined" onClick={handleBack} sx={{ mb: 2 }}>
                    ← Back to List
                </Button>

                {/* Status Messages */}
                {(message || error) && (
                    <Box mb={2}>
                        {message && (
                            <Alert severity="info" sx={{ mb: error ? 1 : 0 }}>
                                {message}
                            </Alert>
                        )}
                        {error && (
                            <Alert severity="error">
                                {error}
                            </Alert>
                        )}
                    </Box>
                )}

                <Grid container spacing={3}>
                    {/* Basic Website Information */}
                    <Grid item xs={12}>
                        <Card>
                            <CardHeader title="Website Information" />
                            <CardContent>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={6}>
                                        <Typography>
                                            <strong>Domain:</strong>{' '}
                                            <Link 
                                                href={`https://${website.domain}`} 
                                                target="_blank" 
                                                rel="noopener noreferrer"
                                                sx={{ textDecoration: 'none' }}
                                            >
                                                {website.domain} ↗
                                            </Link>
                                        </Typography>
                                        <Typography><strong>ID:</strong> {website.id}</Typography>
                                        <Typography><strong>Rank:</strong> {website.rank}</Typography>
                                        <Typography><strong>WordPress:</strong> {website.is_wordpress ? 'Yes' : 'No'}</Typography>
                                        <Typography><strong>WooCommerce:</strong> {website.is_woocommerce ? 'Yes' : 'No'}</Typography>
                                        <Typography><strong>Posts:</strong> {website.number_of_posts}</Typography>
                                        <Typography>
                                            <strong>Post Growth (30d):</strong> {website.post_growth_30d || 'N/A'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <Typography><strong>Theme:</strong> {website.theme_used || 'N/A'}</Typography>
                                        <Typography>
                                            <strong>Avg Daily Posts:</strong> {
                                                website.avg_daily_posts ? 
                                                Number(website.avg_daily_posts).toFixed(2) : 
                                                'N/A'
                                            }
                                        </Typography>
                                        <Typography>
                                            <strong>Posts Confidence:</strong> {
                                                website.daily_posts_confidence ? 
                                                `${(Number(website.daily_posts_confidence) * 100).toFixed(1)}%` : 
                                                'N/A'
                                            }
                                        </Typography>
                                        <Typography>
                                            <strong>Last Posts Calculation:</strong> {
                                                website.last_posts_calculation ? 
                                                moment(website.last_posts_calculation).format('YYYY-MM-DD HH:mm') : 
                                                'Never'
                                            }
                                        </Typography>
                                        <Typography>
                                            <strong>Last Checked:</strong> {
                                                website.last_checked ? 
                                                moment(website.last_checked).format('YYYY-MM-DD') : 
                                                'Never'
                                            }
                                        </Typography>
                                        <Typography><strong>Profile Status:</strong> {website.profile_status}</Typography>
                                        <Typography>
                                            <strong>Quality Score:</strong> {
                                                website.profile_quality_score ? 
                                                `${(website.profile_quality_score * 100).toFixed(1)}%` : 
                                                'N/A'
                                            }
                                        </Typography>
                                    </Grid>
                                    {website.profile_error && (
                                        <Grid item xs={12}>
                                            <Typography color="error">
                                                <strong>Profile Error:</strong> {website.profile_error}
                                            </Typography>
                                        </Grid>
                                    )}
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>
                    {/* Company Profile */}
                    {companyProfile && (
                        <Grid item xs={12}>
                            <Card>
                                <CardHeader 
                                    title="Company Profile" 
                                    action={
                                        <Button
                                            variant="contained"
                                            onClick={handleGenerateProfile}
                                            disabled={isGenerating}
                                        >
                                            {isGenerating ? (
                                                <>
                                                    <CircularProgress size={20} sx={{ mr: 1 }} />
                                                    Updating Profile...
                                                </>
                                            ) : (
                                                'Update Profile'
                                            )}
                                        </Button>
                                    }
                                />
                                <CardContent>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={6}>
                                            <Typography variant="h6">{companyProfile.company_name}</Typography>
                                            <Typography>Founded: {companyProfile.founded_year || 'N/A'}</Typography>
                                            <Typography>Category: {companyProfile.business_category}</Typography>
                                            <Typography>Size: {companyProfile.estimated_size || 'N/A'}</Typography>
                                            <Typography>Revenue: {companyProfile.annual_revenue_range || 'N/A'}</Typography>
                                            <Typography>
                                                Location: {companyProfile.location ? (
                                                    <>
                                                        {companyProfile.location.city ? `${companyProfile.location.city}, ` : ''}
                                                        {companyProfile.location.state ? `${companyProfile.location.state}, ` : ''}
                                                        {companyProfile.location.country || ''}
                                                    </>
                                                ) : 'N/A'}
                                            </Typography>
                                        </Grid>

                                        <Grid item xs={12} md={6}>
                                            <Typography variant="subtitle1">Social Profiles</Typography>
                                            {companyProfile.social_profiles && Object.entries(companyProfile.social_profiles).map(([platform, url]) => (
                                                <Typography key={platform}>
                                                    <strong>{platform}:</strong>{' '}
                                                    <a href={url} target="_blank" rel="noopener noreferrer">{url}</a>
                                                </Typography>
                                            ))}
                                        </Grid>
                                        {companyProfile.description && (
                                            <Grid item xs={12}>
                                                <Typography variant="subtitle1">Description</Typography>
                                                <Typography>{companyProfile.description}</Typography>
                                            </Grid>
                                        )}
                                        {companyProfile.technologies?.length > 0 && (
                                            <Grid item xs={12}>
                                                <Typography variant="subtitle1">Technologies</Typography>
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                    {companyProfile.technologies.map((tech, index) => (
                                                        <Chip key={index} label={tech} size="small" />
                                                    ))}
                                                </Box>
                                            </Grid>
                                        )}
                                    </Grid>
                                </CardContent>
                            </Card>
                        </Grid>
                    )}

                    {!companyProfile && (
                        <Grid item xs={12}>
                            <Card>
                                <CardHeader title="Company Profile" />
                                <CardContent>
                                    <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
                                        <Typography>
                                            No company profile available for this website.
                                        </Typography>
                                        <Button
                                            variant="contained"
                                            onClick={handleGenerateProfile}
                                            disabled={isGenerating}
                                        >
                                            {isGenerating ? (
                                                <>
                                                    <CircularProgress size={20} sx={{ mr: 1 }} />
                                                    Generating Profile...
                                                </>
                                            ) : (
                                                'Generate Company Profile'
                                            )}
                                        </Button>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    )}

                    {/* Email Introduction - Only show if there's an unsent email or no email */}
                    {companyProfile && (!emailIntroductions?.some(intro => intro.status === 'sent')) && (
                        <Grid item xs={12}>
                            <Card>
                                <CardHeader title="Email Introduction" />
                                <CardContent>
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={4}
                                        variant="outlined"
                                        placeholder="Add any personalization notes here..."
                                        value={personalization}
                                        onChange={(e) => setPersonalization(e.target.value)}
                                        sx={{ mb: 2 }}
                                    />
                                    
                                    {emailIntroduction ? (
                                        <Box>
                                            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                                                Generated: {moment(emailIntroduction.generated_at).format('YYYY-MM-DD HH:mm')}
                                            </Typography>
                                            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                                                Status: {emailIntroduction.status}
                                            </Typography>
                                            
                                            {/* Add Email Subject */}
                                            <TextField
                                                fullWidth
                                                label="Subject"
                                                value={emailIntroduction?.email_content?.subject || ''}
                                                onChange={handleSubjectChange}
                                                sx={{ mb: 2 }}
                                            />

                                            {/* Replace pre with ReactQuill */}
                                            <Box sx={{ mb: 2, quillStyles }}>
                                                <ReactQuill
                                                    value={emailIntroduction?.email_content?.content || ''}
                                                    onChange={handleEmailContentChange}
                                                    theme="snow"
                                                    modules={{
                                                        toolbar: [
                                                            ['bold', 'italic', 'underline'],
                                                            [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                                                            ['clean']
                                                        ]
                                                    }}
                                                    formats={[
                                                        'bold', 'italic', 'underline',
                                                        'list', 'bullet'
                                                    ]}
                                                    style={{
                                                        whiteSpace: 'pre-wrap'  // Add this style
                                                    }}
                                                />
                                            </Box>

                                            <Box sx={{ 
                                                display: 'flex', 
                                                gap: 2, 
                                                mt: 3,
                                                flexWrap: 'nowrap',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                width: '100%'
                                            }}>
                                                <Box sx={{ display: 'flex', gap: 2 }}>
                                                    <Button
                                                        variant="contained"
                                                        color="primary"
                                                        onClick={handleGenerateEmail}
                                                        disabled={isGeneratingEmail}
                                                        startIcon={isGeneratingEmail ? <CircularProgress size={20} /> : null}
                                                        sx={{ whiteSpace: 'nowrap', minWidth: 'auto' }}
                                                    >
                                                        Generate New
                                                    </Button>
                                                    
                                                    <Button
                                                        variant="contained"
                                                        color="success"
                                                        onClick={handleSaveChanges}
                                                        disabled={isGeneratingEmail}
                                                        sx={{ whiteSpace: 'nowrap', minWidth: 'auto' }}
                                                    >
                                                        Save Changes
                                                    </Button>
                                                </Box>

                                                <FormControl sx={{ flexGrow: 1, mx: 2 }}>
                                                    <InputLabel id="contact-select-label">Select Contact</InputLabel>
                                                    <Select
                                                        labelId="contact-select-label"
                                                        value={selectedContactEmail}
                                                        onChange={(e) => setSelectedContactEmail(e.target.value)}
                                                        disabled={!contacts.length}
                                                    >
                                                        {contacts.map((contact) => (
                                                            <MenuItem key={contact.id} value={contact.email}>
                                                                {contact.name} ({contact.email})
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>

                                                <Button
                                                    variant="contained"
                                                    color="primary"
                                                    onClick={handleSendIntroduction}
                                                    disabled={sendingEmail}
                                                    sx={{ 
                                                        whiteSpace: 'nowrap', 
                                                        minWidth: 'auto',
                                                        px: 3
                                                    }}
                                                >
                                                    {sendingEmail ? 'Sending...' : 'Send Introduction'}
                                                </Button>
                                            </Box>

                                            {emailStatus && (
                                                <Alert 
                                                    severity={emailStatus.type}
                                                    sx={{ mt: 2 }}
                                                >
                                                    {emailStatus.message}
                                                </Alert>
                                            )}
                                        </Box>
                                    ) : (
                                        <Button
                                            variant="contained"
                                            onClick={handleGenerateEmail}
                                            disabled={isGeneratingEmail}
                                            startIcon={isGeneratingEmail ? <CircularProgress size={20} /> : null}
                                        >
                                            Generate Introduction Email
                                        </Button>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>
                    )}

                    {/* Sent Emails and Analytics */}
                    {companyProfile && (
                        <Grid item xs={12}>
                            <Card>
                                <CardHeader title="Sent Email Analytics" />
                                <CardContent>
                                    <TableContainer>
                                        <Table>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Date Sent</TableCell>
                                                    <TableCell>Subject</TableCell>
                                                    <TableCell>Recipient</TableCell>
                                                    <TableCell>Status</TableCell>
                                                    <TableCell>Opens</TableCell>
                                                    <TableCell>Last Opened</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {emailIntroductions?.filter(intro => intro.status === 'sent').map((intro) => {
                                                    // Parse the email content
                                                    const content = typeof intro.email_content === 'string' 
                                                        ? JSON.parse(intro.email_content.replace('```json', '').replace('```', '').trim())
                                                        : intro.email_content;
                                                    
                                                    return (
                                                        <TableRow key={intro.id}>
                                                            <TableCell>
                                                                {intro.sent_at ? moment(intro.sent_at).format('YYYY-MM-DD HH:mm') : 'N/A'}
                                                            </TableCell>
                                                            <TableCell>
                                                                {content?.subject || 'No Subject'}
                                                            </TableCell>
                                                            <TableCell>
                                                                {selectedContactEmail || 'N/A'}
                                                            </TableCell>
                                                            <TableCell>
                                                                <Chip 
                                                                    label={intro.status}
                                                                    color={intro.status === 'sent' ? 'success' : 'default'}
                                                                />
                                                            </TableCell>
                                                            <TableCell>
                                                                {intro.open_count || 0}
                                                            </TableCell>
                                                            <TableCell>
                                                                {intro.last_opened_at ? 
                                                                    moment(intro.last_opened_at).format('YYYY-MM-DD HH:mm') : 
                                                                    'Never'
                                                                }
                                                            </TableCell>
                                                        </TableRow>
                                                    );
                                                })}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </CardContent>
                            </Card>
                        </Grid>
                    )}


                    {/* Contacts */}
                    {contacts && (
                        <Grid item xs={12}>
                            <Card>
                                <CardHeader title="Contacts" />
                                <CardContent>
                                    <TableContainer>
                                        <Table>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Name</TableCell>
                                                    <TableCell>Title</TableCell>
                                                    <TableCell>Email</TableCell>
                                                    <TableCell>Phone</TableCell>
                                                    <TableCell>Added</TableCell>
                                                    <TableCell>Actions</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {/* Add Contact Form Row */}
                                                <TableRow>
                                                    <TableCell>
                                                        <TextField
                                                            size="small"
                                                            value={newContact.name}
                                                            onChange={(e) => setNewContact({...newContact, name: e.target.value})}
                                                            placeholder="Name"
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <TextField
                                                            size="small"
                                                            value={newContact.title}
                                                            onChange={(e) => setNewContact({...newContact, title: e.target.value})}
                                                            placeholder="Title"
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <TextField
                                                            size="small"
                                                            value={newContact.email}
                                                            onChange={(e) => setNewContact({...newContact, email: e.target.value})}
                                                            placeholder="Email"
                                                            required
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <TextField
                                                            size="small"
                                                            value={newContact.phone}
                                                            onChange={(e) => setNewContact({...newContact, phone: e.target.value})}
                                                            placeholder="Phone"
                                                        />
                                                    </TableCell>
                                                    <TableCell></TableCell>
                                                    <TableCell>
                                                        <Button
                                                            variant="contained"
                                                            size="small"
                                                            onClick={handleAddContact}
                                                            disabled={!newContact.email}
                                                            startIcon={<AddIcon />}
                                                        >
                                                            Add
                                                        </Button>
                                                    </TableCell>
                                                </TableRow>
                                                {contacts.map((contact) => (
                                                    <TableRow key={contact.id}>
                                                        <TableCell>{contact.name}</TableCell>
                                                        <TableCell>{contact.title}</TableCell>
                                                        <TableCell>{contact.email}</TableCell>
                                                        <TableCell>{contact.phone}</TableCell>
                                                        <TableCell>
                                                            {contact.created_at ? 
                                                                moment(contact.created_at).format('YYYY-MM-DD') : 
                                                                '-'
                                                            }
                                                        </TableCell>
                                                        <TableCell>
                                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                                <Button
                                                                    size="small"
                                                                    variant="contained"
                                                                    onClick={() => handleGenerateContactProfile(contact.id)}
                                                                    disabled={processingContacts[contact.id]}
                                                                    startIcon={contact.enriched_at ? 
                                                                        <PersonSearchIcon /> : 
                                                                        <PersonAddIcon />
                                                                    }
                                                                >
                                                                    {processingContacts[contact.id] ? (
                                                                        <CircularProgress size={20} />
                                                                    ) : (
                                                                        contact.enriched_at ? "Update Profile" : "Create Profile"
                                                                    )}
                                                                </Button>
                                                                <Button
                                                                    size="small"
                                                                    variant="outlined"
                                                                    color="error"
                                                                    onClick={() => handleDeleteContact(contact.id)}
                                                                >
                                                                    Delete
                                                                </Button>
                                                            </Box>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}                                                
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </CardContent>
                            </Card>
                        </Grid>
                    )}

                    {/* Weekly Metrics */}
                    {weeklyMetrics && weeklyMetrics.length > 0 && (
                        <Grid item xs={12}>
                            <Card>
                                <CardHeader title={`Weekly Metrics (Website ID: ${website.id})`} />
                                <CardContent>
                                    <TableContainer>
                                        <Table>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Date</TableCell>
                                                    <TableCell>Total Posts</TableCell>
                                                    <TableCell>Response Time</TableCell>
                                                    <TableCell>Post ID</TableCell>
                                                    <TableCell>Page ID</TableCell>
                                                    <TableCell>Media ID</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {weeklyMetrics.map((metric) => (
                                                    <TableRow key={metric.id}>
                                                        <TableCell>
                                                            {moment(metric.date).format('YYYY-MM-DD')}
                                                        </TableCell>
                                                        <TableCell>{metric.total_posts}</TableCell>
                                                        <TableCell>{metric.response_time}s</TableCell>
                                                        <TableCell>{metric.post_id}</TableCell>
                                                        <TableCell>{metric.page_id}</TableCell>
                                                        <TableCell>{metric.media_id}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </CardContent>
                            </Card>
                        </Grid>
                    )}

                    <Grid item xs={12}>
                        <Card>
                            <CardHeader title="Debug Information" />
                            <CardContent>
                                <Typography variant="body2">
                                    Website ID: {id}
                                </Typography>
                                <Typography variant="body2">
                                    Company Profile ID: {companyProfile?.id}
                                </Typography>
                                <Typography variant="body2">
                                    Email Introduction ID: {emailIntroduction?.id}
                                </Typography>
                                <Button 
                                    variant="outlined" 
                                    onClick={logState}
                                    sx={{ mt: 2 }}
                                >
                                    Log State to Console
                                </Button>
                            </CardContent>
                        </Card>
                    </Grid>

                </Grid>
            </Box>
        </Box>
    );
};

export default WebsiteDetail;